/**
 * UserActivityTable Component
 * Table showing user activity breakdown with sorting, searching, pagination
 *
 * Story 3.1: User Activity Breakdown
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  User,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Card, Button, Badge } from '../../common';

export type ActivityLevel = 'high' | 'medium' | 'low' | 'inactive';

export interface UserActivityItem {
  userId: string;
  firstName: string;
  lastName: string;
  /** Present only when the caller is entitled to this user's address. */
  email?: string;
  department: string | null;
  role: string;
  okrsOwned: number;
  checkInsSubmitted: number;
  commentsCount: number;
  lastLoginAt: string | null;
  activityLevel: ActivityLevel;
  activityScore: number;
}

export interface UserActivityTableProps {
  /** User activity data */
  data: UserActivityItem[];
  /** Total count for pagination */
  total: number;
  /** Current page */
  page: number;
  /** Page size */
  pageSize: number;
  /** Total pages */
  totalPages: number;
  /** Current sort column */
  sortBy: string;
  /** Current sort order */
  sortOrder: 'asc' | 'desc';
  /** Search query */
  searchQuery: string;
  /** Activity level filter */
  activityLevelFilter?: ActivityLevel;
  /**
   * Whether the current caller may search by email address. The API only matches
   * on email for callers entitled to every address in the tenant; advertising the
   * affordance to anyone else produces a silent dead end — they type an address,
   * get no results, and conclude the person does not exist.
   */
  canSearchByEmail?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Callbacks */
  onPageChange: (page: number) => void;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  onSearchChange: (query: string) => void;
  onActivityLevelFilterChange: (level?: ActivityLevel) => void;
  onExport?: () => void;
  onUserClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Get activity level badge styling
 */
function getActivityLevelBadge(level: ActivityLevel): { variant: 'success' | 'warning' | 'danger' | 'secondary'; label: string } {
  switch (level) {
    case 'high':
      return { variant: 'success', label: 'High' };
    case 'medium':
      return { variant: 'warning', label: 'Medium' };
    case 'low':
      return { variant: 'danger', label: 'Low' };
    case 'inactive':
    default:
      return { variant: 'secondary', label: 'Inactive' };
  }
}

/**
 * Loading skeleton for table
 */
const TableSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="border-b border-stone-100">
        {Array.from({ length: 7 }).map((_, j) => (
          <td key={j} className="px-4 py-3">
            <div className="h-4 bg-stone-200 rounded animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

/**
 * User Activity Table Component
 */
export const UserActivityTable: React.FC<UserActivityTableProps> = ({
  data,
  total,
  page,
  pageSize,
  totalPages,
  sortBy,
  sortOrder,
  searchQuery,
  activityLevelFilter,
  canSearchByEmail = false,
  isLoading = false,
  onPageChange,
  onSortChange,
  onSearchChange,
  onActivityLevelFilterChange,
  onExport,
  onUserClick,
  className,
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Debounced search
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(localSearch);
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      onSortChange(column, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(column, 'desc');
    }
  };

  // Render sort indicator
  const renderSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ChevronUp className="w-4 h-4 text-stone-300" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-primary-600" />
    ) : (
      <ChevronDown className="w-4 h-4 text-primary-600" />
    );
  };

  const activityLevels: ActivityLevel[] = ['high', 'medium', 'low', 'inactive'];

  return (
    <Card className={clsx('overflow-hidden', className)}>
      {/* Header with search and filters */}
      <div className="p-4 border-b border-stone-200">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                placeholder={
                  canSearchByEmail ? 'Search by name or email...' : 'Search by name...'
                }
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </form>

          {/* Filters and actions */}
          <div className="flex items-center gap-3">
            {/* Activity level filter */}
            <select
              value={activityLevelFilter || ''}
              onChange={(e) =>
                onActivityLevelFilterChange(e.target.value as ActivityLevel || undefined)
              }
              className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Activity Levels</option>
              {activityLevels.map((level) => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </option>
              ))}
            </select>

            {/* Export button */}
            {onExport && (
              <Button variant="secondary" size="sm" onClick={onExport}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-stone-50">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-stone-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  User
                  {renderSortIcon('name')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                Department
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-stone-100"
                onClick={() => handleSort('okrsOwned')}
              >
                <div className="flex items-center gap-1">
                  OKRs
                  {renderSortIcon('okrsOwned')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-stone-100"
                onClick={() => handleSort('checkInsSubmitted')}
              >
                <div className="flex items-center gap-1">
                  Check-ins
                  {renderSortIcon('checkInsSubmitted')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-stone-100"
                onClick={() => handleSort('commentsCount')}
              >
                <div className="flex items-center gap-1">
                  Comments
                  {renderSortIcon('commentsCount')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-stone-100"
                onClick={() => handleSort('lastLoginAt')}
              >
                <div className="flex items-center gap-1">
                  Last Login
                  {renderSortIcon('lastLoginAt')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider cursor-pointer hover:bg-stone-100"
                onClick={() => handleSort('activityScore')}
              >
                <div className="flex items-center gap-1">
                  Activity
                  {renderSortIcon('activityScore')}
                </div>
              </th>
            </tr>
          </thead>

          {isLoading ? (
            <TableSkeleton rows={10} />
          ) : data.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <User className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No users found</p>
                  {searchQuery && (
                    <p className="text-sm text-stone-400 mt-1">
                      Try adjusting your search or filters
                    </p>
                  )}
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {data.map((user) => {
                const badge = getActivityLevelBadge(user.activityLevel);
                return (
                  <tr
                    key={user.userId}
                    className={clsx(
                      'border-b border-stone-100 transition-colors',
                      onUserClick && 'cursor-pointer hover:bg-stone-50'
                    )}
                    onClick={() => onUserClick?.(user.userId)}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-stone-900">
                          {user.firstName} {user.lastName}
                        </div>
                        {/*
                          Absent unless the caller is entitled to this address
                          (own record, admin, or a direct report). Render nothing
                          rather than a placeholder: the row is complete without it.
                        */}
                        {user.email && (
                          <div className="text-sm text-stone-500">{user.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">
                      {user.department || <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-stone-900">
                      {user.okrsOwned}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-stone-900">
                      {user.checkInsSubmitted}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-stone-900">
                      {user.commentsCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">
                      {user.lastLoginAt ? (
                        formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })
                      ) : (
                        <span className="text-stone-400">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={badge.variant} size="sm">
                        {badge.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-stone-200 flex items-center justify-between">
          <div className="text-sm text-stone-500">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} users
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-stone-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default UserActivityTable;
